import type { ReactNode } from 'react';
import { Link, useLocation, useRouter, type LinkProps } from '@tanstack/react-router';
import {
  AddressNotFound,
  Button,
  buttonClasses,
  type AddressSuggestion,
} from '@evogard/ui';
import { useSessionContext } from '../lib/session.js';
import { activeModule, visibleModules, type MenuModule } from '../menu.js';

/** Quantas telas a sugestão mostra. O resto está no menu ao lado. */
const SUGGESTIONS = 6;

/**
 * O "não existe" da interna, para o endereço e para o registro — a anatomia é
 * `AddressNotFound`, de `@evogard/ui`, e aqui ficam o roteador e a escolha das
 * sugestões. Quem chama abre o `<Page>` em volta.
 *
 * As sugestões já vêm sem o que a pessoa não pode abrir (`visibleModules`, a
 * mesma poda do menu): o endereço que aponta um módulo (`/erp/veiculo`,
 * `/erp/veiculos/VE-999999`) sugere as telas daquele módulo; o que não aponta
 * nenhum sugere os módulos.
 */
export function NotFoundContent({
  title,
  hint,
  primary,
}: {
  /** O que não foi achado. Sem ele, "Página não encontrada". */
  title?: string;
  /** O porquê provável e o que fazer. */
  hint?: ReactNode;
  /** A saída principal — a lista do assunto. Sem ela, o início. */
  primary?: { to: LinkProps['to']; search?: LinkProps['search']; label: string };
}) {
  const path = useLocation({ select: (l) => l.pathname });
  const router = useRouter();
  const { data: context } = useSessionContext();

  const modules = (
    context
      ? visibleModules({
          permissions: context.permissions,
          consultant: context.consultant,
        })
      : []
  ).filter((m) => m.id !== 'inicio'); // o prefixo `/` casaria com qualquer endereço
  const near = activeModule(path, modules);

  return (
    <AddressNotFound
      title={title}
      hint={hint}
      path={path}
      suggestions={near ? moduleScreens(near) : allModules(modules)}
      renderLink={({ to, className, children }) => (
        <Link to={to} className={className}>
          {children}
        </Link>
      )}
      actions={
        <>
          {primary ? (
            <Link to={primary.to} search={primary.search} className={buttonClasses()}>
              {primary.label}
            </Link>
          ) : (
            <Link to="/" className={buttonClasses()}>
              Ir para o início
            </Link>
          )}
          {router.history.canGoBack() && (
            <Button variant="secundario" onClick={() => router.history.back()}>
              Voltar à tela anterior
            </Button>
          )}
        </>
      }
    />
  );
}

/** As telas do módulo que o endereço aponta, na ordem do menu. */
function moduleScreens(module: MenuModule) {
  const items: AddressSuggestion[] = module.sections
    .flatMap((s) => s.items)
    .slice(0, SUGGESTIONS)
    .map((item) => ({ to: item.to, label: item.label }));
  return {
    title: `Telas de ${module.label}`,
    description: 'Talvez seja uma destas.',
    items,
  };
}

/** Endereço sem módulo conhecido: os módulos que a pessoa pode abrir. */
function allModules(modules: MenuModule[]) {
  const items: AddressSuggestion[] = modules.map((m) => ({
    to: m.start,
    label: m.label,
  }));
  return { title: 'Módulos', description: 'Os que você pode abrir.', items };
}
